import PropTypes from 'prop-types';
import { AdvancedCombobox } from '@evertrue/et-components';
import Query from 'entities/search/query-parts/query';
import Parameter from 'entities/search/query-parts/parameter-query';
import Property from 'entities/search/query-parts/property-query';

export const ES_FIELD = 'enrichment.donorsearch_scores_summary.estimated_capacity';

export const BUCKETS = Object.freeze([
  { label: '<$25K', value: '<$25K', range: { lt: 25_000 } },
  { label: '$25K–$49K', value: '$25K–$49K', range: { gte: 25_000, lt: 50_000 } },
  { label: '$50K–$99K', value: '$50K–$99K', range: { gte: 50_000, lt: 100_000 } },
  { label: '$100K–$249K', value: '$100K–$249K', range: { gte: 100_000, lt: 250_000 } },
  { label: '$250K–$499K', value: '$250K–$499K', range: { gte: 250_000, lt: 500_000 } },
  { label: '$500K–$999K', value: '$500K–$999K', range: { gte: 500_000, lt: 1_000_000 } },
  { label: '$1M–$2.4M', value: '$1M–$2.4M', range: { gte: 1_000_000, lt: 2_500_000 } },
  { label: '$2.5M–$4.9M', value: '$2.5M–$4.9M', range: { gte: 2_500_000, lt: 5_000_000 } },
  { label: '$5M–$9.9M', value: '$5M–$9.9M', range: { gte: 5_000_000, lt: 10_000_000 } },
  { label: '$10M–$24M', value: '$10M–$24M', range: { gte: 10_000_000, lt: 25_000_000 } },
  { label: '$25M–$49M', value: '$25M–$49M', range: { gte: 25_000_000, lt: 50_000_000 } },
  { label: '$50M–$99M', value: '$50M–$99M', range: { gte: 50_000_000, lt: 100_000_000 } },
  { label: '$100M–$249M', value: '$100M–$249M', range: { gte: 100_000_000, lt: 250_000_000 } },
  { label: '$250M–$499M', value: '$250M–$499M', range: { gte: 250_000_000, lt: 500_000_000 } },
  { label: '$500M–$999M', value: '$500M–$999M', range: { gte: 500_000_000, lt: 1_000_000_000 } },
  { label: '$1B+', value: '$1B+', range: { gte: 1_000_000_000 } },
]);

const OPERATOR_OPTIONS = [
  { label: 'equals', value: 'must' },
  { label: 'does not equal', value: 'must_not' },
  { label: 'is set', value: 'is_set' },
  { label: 'is not set', value: 'is_not_set' },
];

const isExistsOperator = (operatorValue) => operatorValue === 'is_set' || operatorValue === 'is_not_set';

const findBucket = (predicate) => BUCKETS.find(predicate);

const rangeForBucketValue = (bucketValue) => findBucket((b) => b.value === bucketValue)?.range;

/**
 * Build an ES query for the Capacity Based on Wealth filter.
 *
 * - "equals" + N buckets   → should: [range, range, ...]   (OR semantics)
 * - "does not equal" + N   → must_not: [range, range, ...] (none may match)
 * - "is set"               → must: [exists]
 * - "is not set"           → must_not: [exists] (ES treats exists as positive only)
 */
export const buildQuery = (value, field = ES_FIELD) => {
  const operator = value?.parameter?.value;

  if (isExistsOperator(operator)) {
    const parameterName = operator === 'is_set' ? 'must' : 'must_not';
    return Query([Parameter(parameterName, [Property(field, true, { type: 'exists' })])]);
  }

  const ranges = (value?.value || []).map((item) => rangeForBucketValue(item?.value)).filter((range) => range != null);

  if (!ranges.length) return Query([]);

  const properties = ranges.map((range) => Property(field, range, { type: 'object' }));
  const parameterName = operator === 'must_not' ? 'must_not' : 'should';
  return Query([Parameter(parameterName, properties)]);
};

/**
 * Reverse a saved-search criteria back into the filter-row value shape.
 * Matches each {gte, lt} range against BUCKETS to recover the bucket label.
 */
export const parseValue = (criteria) => {
  const mustQuery = criteria.getByParameter('must') || [];
  const mustNotQuery = criteria.getByParameter('must_not') || [];
  const shouldQuery = criteria.getByParameter('should') || [];

  const existsMust = mustQuery.find((item) => item?.operator === 'exists');
  if (existsMust) {
    return { parameter: { label: 'is set', value: 'is_set' } };
  }
  const existsMustNot = mustNotQuery.find((item) => item?.operator === 'exists');
  if (existsMustNot) {
    return { parameter: { label: 'is not set', value: 'is_not_set' } };
  }

  const matchBucket = (range) =>
    findBucket((b) => (b.range.gte ?? null) === (range?.gte ?? null) && (b.range.lt ?? null) === (range?.lt ?? null));

  const recoverFrom = (queryList) =>
    queryList
      .map((item) => matchBucket(item?.value))
      .filter(Boolean)
      .map((bucket) => ({ label: bucket.label, value: bucket.value }));

  if (mustNotQuery.length) {
    const buckets = recoverFrom(mustNotQuery);
    if (buckets.length) {
      return { parameter: { label: 'does not equal', value: 'must_not' }, value: buckets };
    }
  }

  if (shouldQuery.length) {
    const buckets = recoverFrom(shouldQuery);
    if (buckets.length) {
      return { parameter: { label: 'equals', value: 'must' }, value: buckets };
    }
  }

  return {};
};

const CapacityWealthFilter = ({ value, onChange }) => {
  const operator = value?.parameter || { label: 'equals', value: 'must' };
  const showBuckets = !isExistsOperator(operator.value);

  const handleOperatorChange = (newOperator) => {
    onChange({ ...value, parameter: newOperator });
  };

  const handleBucketsChange = (selected) => {
    onChange({ ...value, value: selected });
  };

  return (
    <div className="segment-filter-row--val-wrapper">
      <div className="segment-filter-row--col segment-filter-row--operator-value" style={{ width: '400px' }}>
        <AdvancedCombobox
          placeholder="Select operator"
          options={OPERATOR_OPTIONS}
          value={operator}
          onChange={handleOperatorChange}
        />
      </div>
      {showBuckets ? (
        <div className="segment-filter-row--sub-value segment-filter-row--min-width">
          <AdvancedCombobox
            placeholder="Select capacity ranges..."
            options={BUCKETS}
            value={value?.value || []}
            multiple
            onChange={handleBucketsChange}
          />
        </div>
      ) : null}
    </div>
  );
};

CapacityWealthFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({
    parameter: PropTypes.object,
    value: PropTypes.array,
  }),
};

CapacityWealthFilter.defaultProps = {
  value: { parameter: { label: 'equals', value: 'must' } },
};

export default CapacityWealthFilter;
